import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Container,
  Modal,
  Label,
  Input,
} from "reactstrap";
import { useTable, useGlobalFilter, usePagination } from "react-table";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import moment from "moment";

import Pagination from "components/Common/Pagination";
import { fetchNodeData, FetchNodePostData } from "utils/common-fetchData";
import secureLocalStorage from "react-secure-storage";
import Loader from "pages/Separate/Loader";
import configure from "configure";
import Breadcrumbs from "components/Common/Breadcrumb";
import TDSInvoice from "../../../assets/images/TDSInvoice.jpg";
import { colourStyles } from "utils/common-options";
import Select from "react-select";

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (val) => Number(val || 0).toFixed(2);

const isBetween = (date, start, end) => {
  const d = moment(date);
  if (start && end) return d.isBetween(moment(start), moment(end), null, "[]");
  if (start) return d.isSameOrAfter(moment(start));
  if (end) return d.isSameOrBefore(moment(end));
  return true;
};

const currentFY = () => {
  const now = moment();
  const year = now.month() >= 3 ? now.year() : now.year() - 1;
  return {
    start: moment(`${year}-04-01`).startOf("day"),
    end: moment(`${year + 1}-03-31`).endOf("day"),
  };
};

const prevFY = () => {
  const { start } = currentFY();
  const year = start.year() - 1;
  return {
    start: moment(`${year}-04-01`).startOf("day"),
    end: moment(`${year + 1}-03-31`).endOf("day"),
  };
};

const types = {
  invoice: {
    label: "Invoice",
    color: "bg-success",
  },
  ["vendor pay"]: {
    label: "Vendor Pay",
    color: "bg-warning",
  },
  expense: {
    label: "Expense",
    color: "bg-primary",
  },
};

// ── TableContainer ────────────────────────────────────────────────────────────
function TableContainer({ columns, data, tdsType }) {
  const navigate = useNavigate();

  const [fixedContent, setFixedContent] = useState(false);
  const [formPopup, setFormPopup] = useState(false);
  const [filteredData, setFilteredData] = useState(data);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    type: [],
  });
  const [activeFilter, setActiveFilter] = useState(false);

  // ── react-table ─────────────────────────────────────────────────────────────
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    state,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    pageOptions,
    setGlobalFilter,
    prepareRow,
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: 0, pageSize: 30 },
      autoResetPage: false,
    },
    useGlobalFilter,
    usePagination,
  );

  const { pageIndex, pageSize, globalFilter } = state;

  // sync when parent data changes
  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  useEffect(() => {
    gotoPage(0);
  }, [tdsType]);

  // scroll listener
  useEffect(() => {
    const onScroll = () => setFixedContent(window.scrollY > 226);
    document.addEventListener("scroll", onScroll);
    return () => document.removeEventListener("scroll", onScroll);
  }, []);

  // ── Filter ──────────────────────────────────────────────────────────────────
  const applyFilter = () => {

    let temp = [...data];

    // DATE FILTER
    if (filters.startDate || filters.endDate) {
      temp = temp.filter((d) =>
        isBetween(d.date, filters.startDate, filters.endDate),
      );
    }

    // TYPE FILTER (MULTI SELECT)
    if (filters.type?.length) {
      const selectedTypes = filters.type.map((i) => i.value);

      temp = temp.filter((d) => {
        // expense & vendor pay = Debit
        if (selectedTypes.includes("Debit") && d.type !== "invoice")
          return true;

        // invoice = Credit
        if (selectedTypes.includes("Credit") && d.type === "invoice")
          return true;

        return false;
      });
    }
    gotoPage(0);
    setFilteredData(temp);
    setFormPopup(false);
    setActiveFilter(true);
  };

  const clearFilter = () => {
    setFilters({ startDate: "", endDate: "", type: [] });
    setFilteredData(data);
    setFormPopup(false);
    setActiveFilter(false);
  };

  // ── Export ──────────────────────────────────────────────────────────────────
  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      filteredData.map((item) => ({
        Date: moment(item.date).format("DD-MM-YYYY"),
        "TDS Receive Date": item.tdsReceiveDate
          ? moment(item.tdsReceiveDate).format("DD-MM-YYYY")
          : "--",
        Title: item.title,
        "Invoice No": item.tax_inv_no || item.invoice_number || "--",
        Customer: item.customer?.company || "--",
        Category: item.category || "--",
        Project: item.project || "--",
        Type: item.type,
        "Credit (Invoice)": item.type === "invoice" ? fmt(item.tdsValue) : "--",
        "Debit (Exp/Vendor)":
          item.type !== "invoice" ? fmt(item.tdsValue) : "--",
      })),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TDS");
    XLSX.writeFile(wb, "ztpl_tds.xlsx");
  };

  // ── Balance calculations ────────────────────────────────────────────────────
  const calcBalance = (rows) => {
    let credit = 0,
      debit = 0;
    rows.forEach((item) => {
      if (item.type === "invoice") credit += Number(item.tdsValue || 0);
      else debit += Number(item.tdsValue || 0);
    });
    return { credit, debit, net: credit - debit };
  };

  const currentMonthBalance = useMemo(() => {
    const rows = filteredData.filter((d) =>
      moment(d.date).isSame(moment(), "month"),
    );
    return calcBalance(rows);
  }, [filteredData]);

  const { start: fyStart, end: fyEnd } = currentFY();
  const currentYearBalance = useMemo(() => {
    const rows = filteredData.filter((d) =>
      moment(d.date).isBetween(fyStart, fyEnd, null, "[]"),
    );
    return calcBalance(rows);
  }, [filteredData]);

  const { start: pfyStart, end: pfyEnd } = prevFY();
  const previousYearBalance = useMemo(() => {
    const rows = filteredData.filter((d) =>
      moment(d.date).isBetween(pfyStart, pfyEnd, null, "[]"),
    );
    return calcBalance(rows);
  }, [filteredData]);

  // ── Balance Card ────────────────────────────────────────────────────────────
  const BalanceCard = ({ label, balance }) => (
    <div
      className="budget_status py-2 px-2 rounded proposal-status text-center w-auto"
    // style={{ borderTop: `3px solid ${balance.net >= 0 ? "#34c38f" : "#f46a6a"}` }}
    >
      <p className="mb-1 text-muted fw-bold" style={{ fontSize: 12 }}>
        {label}
      </p>
      <div className="d-flex gap-3 justify-content-center">
        <small className="text-success">
          <i className="fas fa-arrow-up me-1" style={{ fontSize: 10 }} />₹
          {fmt(balance.credit)}
        </small>
        <small className="text-danger">
          <i className="fas fa-arrow-down me-1" style={{ fontSize: 10 }} />₹
          {fmt(balance.debit)}
        </small>
      </div>
      <h5
        className={`mb-0 mt-1 ${balance.net >= 0 ? "text-success" : "text-danger"
          }`}
      >
        ₹ {fmt(Math.abs(balance.net))}
        <small style={{ fontSize: 11, marginLeft: 4 }}>
          {balance.net >= 0 ? "(Net Credit)" : "(Net Debit)"}
        </small>
      </h5>
    </div>
  );

  return (
    <div>
      {/* Fixed scroll buttons */}
      <div
        className={`flex-column fixed-buttons ${fixedContent ? "d-flex" : "d-none"
          }`}
      >
        <button
          onClick={exportExcel}
          className="btn btn-success border-radius fix-btn btn-label"
        >
          <i className="fas fa-download me-2 icon-center label-icon" />
          <span>Export</span>
        </button>
        <button
          onClick={() => navigate(-1)}
          className="btn btn-red border-radius fix-btn btn-label"
        >
          <i className="dripicons-cross me-2 icon-center label-icon" />
          <span>Cancel</span>
        </button>
      </div>

      {/* ── Balance cards ─────────────────────────────────────────────────── */}
      <Row className="mb-3 align-items-start">
        <Col>
          <div className="d-flex gap-3 flex-wrap mb-3">
            <BalanceCard
              label="Current Month Balance"
              balance={currentMonthBalance}
            />
            <BalanceCard
              label="Current Year Balance"
              balance={currentYearBalance}
            />
            <BalanceCard
              label="Previous Year Balance"
              balance={previousYearBalance}
            />
          </div>
        </Col>

        {/* ── Toolbar ───────────────────────────────────────────────────────── */}
        <Col
          md={6}
          className="d-flex gap-2 justify-content-between align-items-center"
        >
          <div className="d-flex gap-3 align-items-center justify-content-end w-100">
            <div>
              <select
                className="border-radius form-select"
                style={{ width: 100 }}
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {[10, 20, 30, 40, 50].map((s) => (
                  <option key={s} value={s}>
                    Show {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="search-box d-inline-block">
              <div className="position-relative">
                <input
                  type="text"
                  className="border-radius form-control"
                  placeholder="Search..."
                  value={globalFilter || ""}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  style={{ width: "200px" }}
                />
                <i className="bx bx-search-alt search-icon" />
              </div>
            </div>

            <button
              onClick={() => setFormPopup(!formPopup)}
              className={`btn ${activeFilter ? "btn-success" : "btn-outline-secondary"
                } `}
            >
              <i className="fas fa-filter"></i>{" "} 
            </button>

            <button
              onClick={exportExcel}
              className="border-radius btn btn-success text-white btn-label"
            >
              <i className="fas fa-download me-2 label-icon" /> Export
            </button>

            <button
              onClick={() => navigate(-1)}
              className="border-radius btn btn-red btn-label"
            >
              <i className="dripicons-cross me-2 label-icon" /> Cancel
            </button>
          </div>
        </Col>
      </Row>

      {/* ── Filter popup ──────────────────────────────────────────────────── */}
      {formPopup && (
        <div className="p-4 mb-3 rounded border bg-light">
          <Form onSubmit={(e) => e.preventDefault()}>
            <Row className="align-items-end">
              <div className="col-md-2">
                <label>Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.target.value })
                  }
                />
              </div>
              <div className="col-md-2">
                <label>End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters({ ...filters, endDate: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4">
                <label>Type</label>
                <Select
                  isMulti
                  className="basic-multi-select"
                  classNamePrefix=" select"
                  placeholder="--- Please Select ---"
                  value={filters.type}
                  styles={colourStyles}
                  options={[
                    {
                      value: "Debit",
                      label: "Debit (Expense / Vendor Pay)",
                    },
                    {
                      value: "Credit",
                      label: "Credit (Invoice)",
                    },
                  ]}
                  onChange={(e) => setFilters({ ...filters, type: e })}
                />
                {/* <select
                  className="form-select"
                  value={filters.type}
                  onChange={(e) =>
                    setFilters({ ...filters, type: e.target.value })
                  }
                >
                  <option>Nothing Selected</option>
                  <option value="Debit">Debit (Expense / Vendor Pay)</option>
                  <option value="Credit">Credit (Invoice)</option>
                </select> */}
              </div>
              <div className="text-center col-md-4">
                <button
                  className="btn border-radius btn-purple me-3"
                  onClick={(e) => {
                    applyFilter();
                  }}
                >
                  <i className="fas fa-search me-2"></i> Search
                </button>

                <button
                  className="btn border-radius btn-warning me-3"
                  onClick={(e) => {
                    clearFilter();
                  }}
                >
                  Clear
                </button>
                <button
                  className="btn border-radius btn-red "
                  onClick={(e) => {
                    setFormPopup(false);
                  }}
                >
                  <i className="dripicons-cross me-1"></i> Close
                </button>
              </div>
              {/* <div className="col-md-3 d-flex gap-2">
                <button
                  className="btn border-radius btn-purple"
                  onClick={applyFilter}
                >
                  <i className="fas fa-search me-1" /> Search
                </button>
                <button
                  className="btn border-radius btn-red"
                  onClick={clearFilter}
                >
                  <i className="dripicons-cross me-1" /> Clear
                </button>
              </div> */}
            </Row>
          </Form>
        </div>
      )}

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <div className="position-relative">
        <Table className="table table-bordered mt-3" {...getTableProps()}>
          <thead className={fixedContent ? "fix-headers" : ""}>
            {headerGroups.map((hg, i) => (
              <tr key={i} {...hg.getHeaderGroupProps()}>
                {hg.headers.map((col, j) => (
                  <th
                    key={j}
                    className={col.className}
                    {...col.getHeaderProps()}
                  >
                    {col.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center text-muted py-4"
                >
                  No records found.
                </td>
              </tr>
            ) : (
              page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr key={i} className="row-hover" {...row.getRowProps()}>
                    {row.cells.map((cell, j) => (
                      <td
                        key={j}
                        className={cell.column.className}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* ── Pagination ────────────────────────────────────────────────────── */}
      <Row className="justify-content-md-end justify-content-center align-items-center">
        <Pagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          canPreviousPage={canPreviousPage}
          nextPage={nextPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
        />
      </Row>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const ZtplTdsPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tdsReceiveModal, setTdsReceiveModal] = useState(false);
  const [record, setRecord] = useState({});
  const [recordedAmt, setRecordedAmt] = useState(0);

  const [tdsForm, setTdsForm] = useState({
    date: "",
    amount: "",
  });
  const [tdsRecords, setTdsRecords] = useState([]);

  const allowedTypes = ["ztpl", "zithas"];
  const { tdsType = "ztpl" } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!allowedTypes.includes(tdsType)) {
      return navigate(`${configure.appUrl}not-found`);
    }
    if (!tdsReceiveModal) {
      (async () => {
        setLoading(true);
        const token = secureLocalStorage.getItem("auth_token");

        const res = await fetchNodeData(
          false,
          `gst/tds/${tdsType}/${configure.location[0]}`,
          token,
        );

        if (res?.success) {
          const formatted = (res.data || [])
            .map((item) => ({
              id: item.id,
              date: item.date,
              tdsReceiveDate: item.tdsReceiveDate || null,
              title: item.title,
              tax_inv_no: item.tax_inv_no || "--",
              invoice_number: item.invoice_number || "--",
              customer: item.customer || {},
              category: item.category || "--",
              project: item.project || "--",
              type: item.type,
              tdsValue: Number(item.tdsValue || 0),
              recordedAmt: Number(item.recordedAmt || 0),
            }))
            .sort(
              (a, b) =>
                moment(b.date, "YYYY-MM-DD").valueOf() -
                moment(a.date, "YYYY-MM-DD").valueOf(),
            );

          setData(formatted);
        }

        setLoading(false);
      })();
    }
  }, [tdsType, tdsReceiveModal]);

  const fetchTdsRecords = async (record) => {
    try {
      const res = await fetchNodeData(
        setLoading,
        `gst/tds/get-records?data_id=${record.id}&type=${tdsType}&data_type=${record.type}`,
        secureLocalStorage.getItem("auth_token"),
      );

      if (res.success) {
        setTdsRecords(res.data || []);
        setRecordedAmt(res.totalAmount || 0);
      }
    } catch (error) {
      console.error("TDS fetch error:", error);
    }
  };

  function getStatus(record) {
    if (record.recordedAmt === record.tdsValue)
      return (
        <button className="bg-success me-2 badge border-0 fw-bold">
          {record.type === "invoice" ? "Received" : "Paid"}
        </button>
      );

    if (record.recordedAmt < record.tdsValue && record.recordedAmt > 0)
      return (
        <button className="bg-warning me-2 badge border-0 fw-bold">Partial</button>
      );

    if (record.recordedAmt === 0)
      return (
        <button className="bg-danger me-2 badge border-0 fw-bold">Pending</button>
      );

    return null;
  }


  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        className: "text-center",
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: "Date",
        accessor: "date",
        className: "text-center",
        Cell: ({ row }) =>
          moment(row.original.date).isValid()
            ? moment(row.original.date).format("DD-MM-YYYY")
            : "--",
      },
      // {
      //   Header: "TDS Receive",
      //   accessor: "tdsReceiveDate",
      //   className: "text-center",
      //   Cell: ({ row }) =>
      //     row.original.tdsReceiveDate &&
      //     moment(row.original.tdsReceiveDate).isValid() ? (
      //       moment(row.original.tdsReceiveDate).format("DD-MM-YYYY")
      //     ) : (
      //       <span className="text-muted">--</span>
      //     ),
      // },
      {
        Header: "Title",
        accessor: "title",
        className: "text-start",
      },
      {
        Header: "Invoice No",
        accessor: "tax_inv_no",
        className: "text-center",
        Cell: ({ row }) =>
          row.original.tax_inv_no !== "--"
            ? row.original.tax_inv_no
            : row.original.invoice_number,
      },
      {
        Header: "Customer / Project",
        accessor: "customer",
        className: "text-start",
        Cell: ({ row }) => {
          const company = row.original.customer?.company;
          const project = row.original.project;
          return (
            <div>
              {company && (
                <div className="fw-semibold" style={{ fontSize: 13 }}>
                  {company}
                </div>
              )}
              {project && project !== "--" && (
                <div className="text-muted">{project}</div>
              )}
            </div>
          );
        },
      },
      {
        Header: "Category",
        accessor: "category",
        className: "text-center",
      },

      {
        Header: "Type",
        accessor: "type",
        className: "text-center",
        Cell: ({ row }) => {
          // const type = row.original.type;
          // const label =
          //   type === "invoice"
          //     ? "Invoice"
          //     : type === "vendor pay"
          //     ? "Vendor Pay"
          //     : "Expense";
          // const cls =
          //   type === "invoice" ? "bg-success" : "bg-warning text-dark";
          // return <span className={`badge ${cls}`}>{label}</span>;


          const type = row.original.type;

          return (
            <span className={`badge ${types[type]?.color}`}>
              {types[type]?.label || "--"}
            </span>
          );
        },
      },

      {
        Header: () => <span className="text-danger">Debit (-)</span>,
        accessor: "debit",
        className: "text-center col-1",
        Cell: ({ row }) =>
          row.original.type !== "invoice" ? (
            <span className="text-danger fw-semibold">
              ₹ {fmt(row.original.tdsValue)}
            </span>
          ) : (
            <span className="text-muted">--</span>
          ),
      },
      {
        Header: () => <span className="text-success">Credit (+)</span>,
        accessor: "credit",
        className: "text-center col-1",
        Cell: ({ row }) =>
          row.original.type === "invoice" ? (
            <span className="text-success fw-semibold">
              ₹ {fmt(row.original.tdsValue)}
            </span>
          ) : (
            <span className="text-muted">--</span>
          ),
      },
      {
        Header: "Action",
        accessor: "action",
        className: "text-center col-1",
        Cell: (cellProps) => {
          const selected = cellProps.row.original;

          return (
            <div className="d-flex align-items-center">
              {getStatus(selected)}
              <button
                onClick={() => {
                  setRecord(selected);
                  setTdsReceiveModal(true);
                  fetchTdsRecords(selected);
                }}
                title={`TDS ${selected?.type === "invoice" ? "Receive" : "Pay"
                  }`}
                className="btn btn-sm btn-outline-secondary"
              >
                <i className="fas fa-pager fs-5"></i>
              </button>
            </div>
          );
        },
      },
    ],
    [tdsType],
  );

  async function handleTdsRecord(e) {
    e.preventDefault();

    swal({
      title: "Are you sure?",
      text: "You want to add tds record!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willProceed) => {
      if (willProceed) {
        try {
          const payload = {
            data_id: record?.id, // current invoice/expense id
            type: tdsType, // or "ztpl" (set dynamically if needed)
            tds_type: record?.type === "invoice" ? "Received" : "Deducted",
            data_type: record?.type, // invoice / expense / vendor pay
            date: tdsForm.date,
            amount: tdsForm.amount,
          };

          const res = await FetchNodePostData(
            setLoading,
            "gst/tds-record", // your API route
            secureLocalStorage.getItem("auth_token"),
            payload,
          );

          if (res.data.success) {
            // reset form
            setTdsForm({ date: "", amount: "" });

            // optional: refresh records list
            fetchTdsRecords(record);

            swal("", "TDS Record added successfully", "success");
          }
        } catch (error) {
          console.error("TDS submit error:", error);
        }
      }
    });
  }

  const pendingAmt = Number(record?.tdsValue - recordedAmt);

  return (
    <React.Fragment>
      <div className="page-content mb-4">
        <Container fluid>
          <Breadcrumbs title={"Dashboard"} breadcrumbItem={`${tdsType} TDS`} />

          <Row>
            <div className="col-12 ps-0">
              {loading && <Loader />}
              <Row className="ms-0 pe-0">
                <Col lg={12} className="p-0">
                  <Card>
                    <CardBody>
                      <TableContainer
                        columns={columns}
                        data={data}
                        tdsType={tdsType}
                      />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </Row>
        </Container>
      </div>

      <Modal isOpen={tdsReceiveModal} size="xl" centered>
        <div className="modal-header">
          <h5 className="modal-title mt-0">
            TDS {record?.type === "invoice" ? "Receive" : "Paid"}
            <span className="text-capitalize">
              {" "}
              ( {record?.title || null} )
            </span>
          </h5>
          <button
            type="button"
            onClick={() => {
              setTdsReceiveModal(false);
              setTdsForm({
                date: "",
                amount: "",
              });
            }}
            className="btn-modal-close"
          >
            <span className="btn-modal-span">&times;</span>
          </button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleTdsRecord}>
            <Row className={"align-items-start"}>
              <div className="col-md-6  ps-3 ">
                <div
                  className="position-relative"
                  style={{
                    maxHeight: "350px",
                    overflow: "auto",
                    scrollbarWidth: "thin",
                  }}
                >
                  <table className="table table-bordered">
                    <thead className="table-light position-sticky w-100 top-0 left-0 right-0">
                      <tr>
                        <th>ID</th>
                        <th>Date</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tdsRecords.length > 0 ? (
                        <>
                          {tdsRecords.map((item, index) => (
                            <tr key={item.id}>
                              <td>{index + 1}</td>

                              <td>
                                {item.date && moment(item.date).isValid()
                                  ? moment(item.date).format("DD-MM-YYYY")
                                  : "--"}
                              </td>

                              <td>₹ {Number(item.amount || 0).toFixed(2)}</td>
                            </tr>
                          ))}

                          <tr>
                            <th colSpan={2}>
                              Total{" "}
                              {record?.type === "invoice" ? "Received" : "Paid"}
                            </th>
                            <th>
                              ₹{" "}
                              {tdsRecords
                                .reduce(
                                  (acc, curr) => acc + Number(curr.amount),
                                  0,
                                )
                                ?.toFixed(2)}
                            </th>
                          </tr>
                        </>
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center text-muted">
                            No Records Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="col-md-6 ps-4">
                <div className="mb-4 ps-2">
                  <div className="d-flex gap-4">
                    <h5 className="mb-4 fw-bold text-dark">
                      Total Amt. - ₹ {record?.tdsValue?.toFixed(2)}
                    </h5>
                    <h5 className="mb-4 fw-bold text-danger">
                      Pending Amt. - ₹ {pendingAmt?.toFixed(2)}
                    </h5>
                  </div>
                  <h5>TDS Records</h5>
                </div>

                <div className="col-md-12  ps-2 mb-4 d-flex">
                  <div className="col-md-7">
                    <Label htmlFor="formrow-firstname-Input">
                      Select Date <span className="text-danger">*</span>
                    </Label>
                    <div className="d-flex justify-content-start align-items-center gap-2">
                      <input
                        type="date"
                        className="form-control"
                        required
                        value={tdsForm.date}
                        disabled={!pendingAmt}
                        onChange={(e) =>
                          setTdsForm({ ...tdsForm, date: e.target.value })
                        }
                        max={moment().format("YYYY-MM-DD")}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-12  ps-2 mb-3 d-flex">
                  <div className="col-md-4">
                    <Label htmlFor="formrow-firstname-Input">
                      Enter Amount <span className="text-danger">*</span>
                    </Label>
                    <div className="d-flex justify-content-start align-items-center gap-2">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Amount"
                        max={pendingAmt || 0}
                        disabled={!pendingAmt}
                        required
                        value={tdsForm.amount}
                        onChange={(e) =>
                          setTdsForm({ ...tdsForm, amount: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Row>

            <div className="text-end">
              <button className="btn btn-save btn-label me-2">
                <i className="fas fa-save me-2 icon-size label-icon me-2"></i>
                Submit
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default ZtplTdsPage;
